import { format } from 'date-fns'
import { TrendingUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback, memo } from 'react'
import { Area, Line, XAxis, YAxis, Tooltip, LineChart, CartesianGrid, ReferenceLine, ResponsiveContainer } from 'recharts'

import { numberWithCommas } from '../../utils'
import { ChartContainer } from '../common/chart'
import { useIsMobile } from '../../hooks/use-mobile'
import { useStableCallback } from '../../hooks/use-stable-callback'

type MinuteGroup = {
  time: string
  date: string
  quantity: number
}

const PaymentTimelineBase = ({ paymentsDataByHour }: { paymentsDataByHour: Array<{ count: number; hour: string }> }) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation(['insights-funding-history-management'])

  const [currentTime, setCurrentTime] = useState(new Date())
  const [xAxisTicks, setXAxisTicks] = useState<Array<string>>([])
  const [yAxisTicks, setYAxisTicks] = useState<Array<number>>([])
  const [paymentsQuantity, setPaymentsQuantity] = useState<number>(0)
  const [activeDotIndex, setActiveDotIndex] = useState<number | null>(6)
  const [paymentsTimeline, setPaymentsTimeline] = useState<Array<MinuteGroup>>([])
  const [, /* cursorPosition */ setCursorPosition] = useState<string | null>(null)
  const [timelineData, setTimelineData] = useState<Array<{ time: string; value: number }>>([])

  useEffect(() => {
    if (paymentsDataByHour) {
      handleDisplayChartData()

      let numOfPayments = 0

      for (const paymentByHour of paymentsDataByHour) {
        numOfPayments += paymentByHour.count
      }

      setPaymentsQuantity(numOfPayments)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsDataByHour])

  useEffect(() => {
    const data = []
    const referenceTime = new Date()
    paymentsTimeline.sort((a, b) => handleParseDateTime(a.date, a.time).getTime() - handleParseDateTime(b.date, b.time).getTime())

    for (let i = 0; i < xAxisTicks.length; i++) {
      data.push({
        time: xAxisTicks[i],
        value: paymentsTimeline[i].quantity,
        tooltipData: `${paymentsTimeline[i].date}, Hora: ${paymentsTimeline[i].time}`
      })
    }

    setTimelineData(data)
    setCurrentTime(referenceTime)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xAxisTicks])

  const handleParseDateTime = (dateStr: string, timeStr: string): Date => {
    const [day, month, year] = dateStr.split('/').map(Number)
    const [hour, minute] = timeStr.split(':').map(Number)

    return new Date(year, month - 1, day, hour, minute)
  }

  const handleDisplayChartData = () => {
    const paymentsGrouped = handleGroupDocumentsByMinute(paymentsDataByHour)
    const paymentsQuantityGrouped = paymentsDataByHour.map((paymentByHour) => paymentByHour.count)
    const paymentsHoursGrouped = paymentsGrouped.map((paymentGrouped) => paymentGrouped.time)

    const maximumNumber = handleGetMaxFromNumbers(paymentsQuantityGrouped)
    const { result } = handleGetNormalizedDistance(0, maximumNumber, 4)

    setYAxisTicks(result)
    setXAxisTicks(paymentsHoursGrouped)
    setPaymentsTimeline(paymentsGrouped)
  }

  const handleGetMaxFromNumbers = (nums: number[]): number => {
    return Math.max(...nums)
  }

  const handleGetNormalizedDistance = (min: number, max: number, divider: number): { result: number[]; newMax: number } => {
    if (divider <= 0) {
      throw new Error('Divider must be greater than 0')
    }

    const rawStep = (max - min) / divider
    const step = Math.ceil(rawStep)

    const newMax = min + step * divider

    const result: number[] = []

    for (let i = 0; i <= divider; i++) {
      result.push(min + i * step)
    }

    return { result, newMax }
  }

  const handleGroupDocumentsByMinute = (payments: Array<{ count: number; hour: string }>): Array<MinuteGroup> => {
    const groupsOfPayments: Record<string, MinuteGroup> = {}

    function formatDate(date: Date): string {
      const dd = date.getDate().toString().padStart(2, '0')
      const mm = (date.getMonth() + 1).toString().padStart(2, '0') // Months are 0-indexed
      const yyyy = date.getFullYear()
      return `${dd}/${mm}/${yyyy}`
    }

    function formatTime(date: Date): string {
      const hh = date.getHours().toString().padStart(2, '0')
      const mm = date.getMinutes().toString().padStart(2, '0')
      return `${hh}:${mm}`
    }

    for (const payment of payments) {
      const dateObj = new Date(payment.hour)
      const dateStr = formatDate(dateObj)
      const timeStr = formatTime(dateObj)
      const key = `${dateStr} ${timeStr}`

      if (groupsOfPayments[key]) {
        groupsOfPayments[key].quantity++
      } else {
        groupsOfPayments[key] = {
          date: dateStr,
          time: timeStr,
          quantity: payment.count
        }
      }
    }

    return Object.values(groupsOfPayments)
  }

  const config = {
    value: { color: '#00B686', label: 'Pagos' }
  }

  const CustomTooltip = useCallback(
    ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-xl shadow-lg border border-emerald-100">
            <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium text-gray-500 font-sans flex items-center gap-1`}>{payload[0].payload.tooltipData}</p>
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} font-bold text-emerald-600 font-sans mt-1 flex items-center gap-1`}>
              <TrendingUp
                size={14}
                className="text-emerald-500"
              />
              {payload[0].value >= 1000 ? t('insights.charts.tooltip.lineChartThousands', { paymentCount: payload[0].value }) : t('insights.charts.tooltip.lineChart', { paymentCount: payload[0].value })}
            </p>
          </div>
        )
      }
      return null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile]
  )

  const handleMouseMove = useStableCallback((e: any) => {
    if (e && e.activeTooltipIndex !== undefined) {
      setActiveDotIndex(e.activeTooltipIndex)
      setCursorPosition(e.activeLabel)
    }
  })

  const handleMouseLeave = useStableCallback(() => {
    setActiveDotIndex(6)
    setCursorPosition(null)
  })

  const formattedCurrentTime = format(currentTime, 'HH:mm')

  const renderDot = useCallback(
    (props: any) => {
      const { cx, cy, index } = props

      if (index === activeDotIndex) {
        return (
          <g>
            <circle
              cx={cx}
              cy={cy}
              r={isMobile ? 5 : 7}
              fill="#00B686"
              stroke="#FFFFFF"
              strokeWidth={2}
            />
          </g>
        )
      }

      return <></>
    },
    [activeDotIndex, isMobile]
  )

  const activePointTime = activeDotIndex !== null ? timelineData[activeDotIndex]?.time : timelineData[6]?.time || '10:22'

  return (
    <div
      style={{ height: '400px' }}
      className="border border-[#E5E7EB] bg-[#F9FAFB] rounded-[24px] shadow-sm w-full overflow-hidden"
    >
      <div className="relative flex flex-col h-full w-full px-3 py-4 md:px-6 md:py-6">
        <div className="absolute top-3 md:top-6 right-3 md:right-6 z-10">
          <div className="bg-white rounded-full px-4 md:px-6 py-2.5 md:py-4 shadow-sm border-none flex items-center gap-2">
            <span className={`text-[#737373] font-sans ${isMobile ? 'text-xs' : 'text-sm'}`}>{t('insights.charts.paymentTimeline.title', { formattedCurrentTime, timeAbbreviation: '' })}</span>
            <span className={`font-semibold text-[#737373]-900 ml-1 font-sans ${isMobile ? 'text-xs' : 'text-sm'}`}>
              {t('insights.charts.paymentTimeline.subtitle', { paymentCount: numberWithCommas(paymentsQuantity) })}
            </span>
          </div>
        </div>

        <div className={`flex-1 w-full mt-12 md:mt-16`}>
          <ChartContainer
            config={config}
            className="h-full w-full aspect-auto"
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <LineChart
                data={timelineData}
                margin={{ top: 10, right: 30, left: 15, bottom: 30 }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <defs>
                  <linearGradient
                    id="colorValue"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="#00D1A1"
                      stopOpacity={0.95}
                    />
                    <stop
                      offset="20%"
                      stopColor="#00C597"
                      stopOpacity={0.85}
                    />
                    <stop
                      offset="40%"
                      stopColor="#00B98E"
                      stopOpacity={0.75}
                    />
                    <stop
                      offset="60%"
                      stopColor="#00AA83"
                      stopOpacity={0.6}
                    />
                    <stop
                      offset="80%"
                      stopColor="#009B79"
                      stopOpacity={0.4}
                    />
                    <stop
                      offset="100%"
                      stopColor="#008C6E"
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#E5E7EB"
                  strokeOpacity={0.5}
                />
                <XAxis
                  dataKey="time"
                  axisLine={false}
                  tickLine={false}
                  stroke="#9CA3AF"
                  fontSize={isMobile ? 10 : 12}
                  tickMargin={10}
                  fontFamily="Open Sans, sans-serif"
                  ticks={xAxisTicks}
                  interval={0}
                  padding={{ left: 15, right: 15 }}
                />
                <YAxis
                  domain={[0, 6]}
                  ticks={yAxisTicks}
                  axisLine={false}
                  tickLine={false}
                  stroke="#9CA3AF"
                  fontSize={isMobile ? 10 : 12}
                  tickMargin={10}
                  fontFamily="Open Sans, sans-serif"
                  interval={0}
                  padding={{ top: 15, bottom: 15 }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={false}
                  isAnimationActive={false}
                />
                <ReferenceLine
                  x={activePointTime}
                  stroke="#00B686"
                  strokeDasharray="3 3"
                  strokeOpacity={0.7}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="none"
                  fillOpacity={1}
                  fill="url(#colorValue)"
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#00B686"
                  strokeWidth={isMobile ? 2 : 3}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="none"
                  dot={renderDot}
                  activeDot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
      </div>
    </div>
  )
}

// Export a memoized version of the component to prevent unnecessary re-renders
export const PaymentTimeline = memo(PaymentTimelineBase)
